import type { GameStateSnapshot, PlayerId, RoomId } from '@juegazo/shared';
import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
} from '@juegazo/shared';

export type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export interface SessionInfo {
  roomId: RoomId | null;
  playerId: PlayerId | null;
  displayName: string | null;
  /**
   * Secret proving ownership of the seat. Issued by the server when the
   * player joins or creates a room, and required to rejoin it. Keeping it
   * next to the player id is what stops another client from taking the
   * seat just by knowing the player id.
   */
  sessionToken: string | null;
}

const SESSION_KEY = 'juegazo.session';

/**
 * Traduce mensajes de error del servidor a versiones amigables para el usuario.
 * El servidor devuelve códigos técnicos (`PARTIDA_NO_EXISTE`, etc.) y mensajes
 * crudos en inglés desde `game-core` que no deberían filtrarse a la UI tal cual.
 */
const SERVER_ERROR_MESSAGES: Record<string, string> = {
  // Códigos del handler de room:rejoin (apps/server/src/index.ts)
  PARTIDA_NO_EXISTE: 'La sala no existe o ya no está disponible',
  PARTIDA_EN_CURSO: 'La partida ya comenzó',
  'Sala no encontrada': 'La sala no existe',
  'Jugador no encontrado': 'No se encontró al jugador en la sala',
  'Estado inválido': 'El estado del juego es inválido',
  'Token de sesión inválido': 'Tu sesión expiró. Volvé a entrar a la sala para continuar',
  // Mensajes de game-core que pueden llegar vía room:error
  'Cannot pass bomb in current state': 'No podés pasar la bomba en este momento',
  'Bomb already exploded': 'La bomba ya explotó',
  'Can only resume from paused': 'Solo se puede reanudar una partida en pausa',
  'Can only pause while playing': 'Solo se puede pausar con la bomba en juego',
  'Not in tiebreak-ready state': 'No hay un desempate listo para iniciar',
  'No tiebreak needed': 'No hace falta un desempate',
  'Cannot return bomb in current state': 'No podés devolver el turno en este momento',
  'No active prompt': 'No hay una consigna activa',
  'Return window expired': 'Se acabó el tiempo para devolver el turno',
};

export function translateServerError(message: string | undefined | null): string {
  if (!message) return 'No se pudo conectar con la sala';
  return SERVER_ERROR_MESSAGES[message] ?? message;
}

export const loadSession = (): SessionInfo => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return { roomId: null, playerId: null, displayName: null, sessionToken: null };
    const parsed = JSON.parse(raw) as Partial<SessionInfo>;
    return {
      roomId: parsed.roomId ?? null,
      playerId: parsed.playerId ?? null,
      displayName: parsed.displayName ?? null,
      sessionToken: typeof parsed.sessionToken === 'string' ? parsed.sessionToken : null,
    };
  } catch {
    return { roomId: null, playerId: null, displayName: null, sessionToken: null };
  }
};

export const saveSession = (info: SessionInfo) => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(info));
};

export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
};

export const createSocket = (): ClientSocket => {
  const url = window.location.origin;
  return io(url, {
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 10,
    transports: ['websocket', 'polling'],
  });
};

export interface GameStoreState {
  connected: boolean;
  state: GameStateSnapshot | null;
  lastError: { code: string; message: string } | null;
  lastFinished: import('@juegazo/shared').FinalResult | null;
  lastExplosion: { playerId: PlayerId; displayName: string } | null;
  lastPlayerLeft: { playerId: PlayerId; displayName: string; reason: 'leave' | 'disconnect' } | null;
  lastPlayerReconnected: { playerId: PlayerId; displayName: string } | null;
}

export type GameStoreListener = (state: GameStoreState) => void;

export class GameStore {
  private socket: ClientSocket | null = null;
  private state: GameStoreState = {
    connected: false,
    state: null,
    lastError: null,
    lastFinished: null,
    lastExplosion: null,
    lastPlayerLeft: null,
    lastPlayerReconnected: null,
  };
  private listeners = new Set<GameStoreListener>();
  private wasDisconnected = false;
  private readonly createSocketFn: () => ClientSocket;
  /**
   * La sala que el cliente tiene en este momento. El snapshot del servidor
   * trae su `roomId`, así que un estado de otra sala se puede descartar.
   */
  private currentRoomId: RoomId | null = null;
  /** La sala que estamos entrando: su id se conoce antes del ack. */
  private expectedRoomId: RoomId | null = null;
  /** Estamos en medio de una entrada a una sala (create/join/rejoin). */
  private enteringRoom = false;
  /**
   * Timer del rejoin automático (el guard de reconexión). Vive acá para poder
   * cancelarse cuando el socket se cae: un rejoin abandonado no puede borrar
   * la sesión más tarde y expulsar al jugador de una sala sana.
   */
  private reconnectGuard: ReturnType<typeof setTimeout> | null = null;
  /**
   * El estado de la sala que teníamos ANTES de la entrada en curso. Si la
   * entrada se rechaza, ese estado es el que hay que restaurar: el cliente
   * sigue con su silla en la sala anterior (el servidor solo libera el
   * asiento viejo cuando la entrada nueva se creó bien).
   */
  private previousRoomState: GameStateSnapshot | null = null;

  constructor(opts: { createSocket?: () => ClientSocket } = {}) {
    this.createSocketFn = opts.createSocket ?? createSocket;
  }

  connect() {
    if (this.socket) return;
    this.socket = this.createSocketFn();
    this.socket.on('connect', () => {
      this.update({ connected: true });
      const session = loadSession();
      if (session.roomId && session.playerId && session.displayName && (!this.state.state || this.wasDisconnected)) {
        let responded = false;
        const guard = setTimeout(() => {
          if (!responded) {
            clearSession();
            this.update({
              lastError: { code: 'reconnect_timeout', message: 'Tiempo agotado' },
              state: null,
            });
          }
        }, 5000);
        this.reconnectGuard = guard;
        this.beginEntering(session.roomId);
        this.socket!.emit(
          'room:rejoin',
          {
            roomId: session.roomId,
            playerId: session.playerId,
            displayName: session.displayName,
            sessionToken: session.sessionToken ?? '',
          },
          (resp) => {
            this.wasDisconnected = false;
            responded = true;
            clearTimeout(guard);
            if (this.reconnectGuard === guard) this.reconnectGuard = null;
            this.endEntering(resp.ok);
            if (resp.ok) {
              this.currentRoomId = resp.state.roomId;
              this.update({ state: resp.state, lastError: null });
            } else {
              clearSession();
              this.currentRoomId = null;
              this.update({
                lastError: { code: 'reconnect_failed', message: translateServerError(resp.error) },
                state: null,
              });
            }
          },
        );
      }
    });
    this.socket.on('connect_error', (err) => {
      const message = err instanceof Error ? err.message : 'Error de transporte';
      this.update({
        lastError: { code: 'socket_connect_error', message },
      });
    });
    this.socket.io.on('reconnect_failed', () => {
      this.update({
        lastError: { code: 'socket_reconnect_failed', message: 'No se pudo reconectar' },
      });
    });
    this.socket.on('disconnect', () => {
      // El rejoin en vuelo se abandona: su timer no debe seguir corriendo y
      // borrar la sesión (y expulsar al jugador) cuando la sala está sana.
      if (this.reconnectGuard) {
        clearTimeout(this.reconnectGuard);
        this.reconnectGuard = null;
      }
      this.wasDisconnected = true;
      this.update({ connected: false });
    });
    this.socket.on('room:state', (snap) => this.applySnapshot(snap));
    this.socket.on('game:state', (snap) => this.applySnapshot(snap));
    this.socket.on('lobby:updated', (snap) => this.applySnapshot(snap));
    this.socket.on('game:exploded', (info) => this.update({ lastExplosion: info }));
    this.socket.on('game:finished', (result) => this.update({ lastFinished: result }));
    this.socket.on('game:paused', () => {
      if (this.state.state) this.update({ state: { ...this.state.state, paused: true, state: 'paused' } });
    });
    this.socket.on('game:resumed', () => {
      if (this.state.state) {
        // Un desempate pausado se reanuda como desempate: no hay que
        // degradarlo a una ronda normal.
        const resumedState =
          this.state.state?.state === 'tiebreak_playing' ? 'tiebreak_playing' : 'playing';
        this.update({ state: { ...this.state.state, paused: false, state: resumedState } });
      }
    });
    this.socket.on('player:left', (info) => this.update({ lastPlayerLeft: info }));
    this.socket.on('player:reconnected', (info) => this.update({ lastPlayerReconnected: info }));
    this.socket.on('room:error', (err) =>
      this.update({
        lastError: { code: err.code, message: translateServerError(err.message) },
      }),
    );
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
  }

  getState = () => this.state;
  getSocket = () => this.socket;

  subscribe = (fn: GameStoreListener): (() => void) => {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  };

  /**
   * Limpia el snapshot del juego. Llamar cuando el usuario abandona una sala
   * para que las emissions tardías del servidor no redirijan la UI.
   */
  clearGameState() {
    this.update({ state: null, lastFinished: null, lastExplosion: null });
  }

  /**
   * Abandona la sala actual: avisa al servidor para que libere la silla y
   * limpia la sesión local. Es el ÚNICO dueño del envío de `room:leave`:
   * todos los caminos de salida (el botón de salir, el escape del lobby, el
   * abandono por timeout y el regreso al inicio) pasan por acá, así el aviso
   * se envía exactamente una vez por salida.
   */
  leaveRoom() {
    this.send('room:leave');
    clearSession();
    this.currentRoomId = null;
    this.expectedRoomId = null;
    this.enteringRoom = false;
    this.previousRoomState = null;
    this.clearGameState();
  }

  /**
   * Limpia el flag de "último jugador que se fue" para que el toast asociado
   * solo se muestre una vez, o para evitar mostrarlo si el jugador ya se
   * reconectó (visibilidad gestionada por app-root).
   */
  clearLastPlayerLeft() {
    if (this.state.lastPlayerLeft) this.update({ lastPlayerLeft: null });
  }

  /**
   * Limpia el flag de "último jugador que volvió a entrar" tras mostrar
   * el toast asociado para evitar mostrarlo dos veces.
   */
  clearLastPlayerReconnected() {
    if (this.state.lastPlayerReconnected) this.update({ lastPlayerReconnected: null });
  }

  private update(partial: Partial<GameStoreState>) {
    this.state = { ...this.state, ...partial };
    for (const l of this.listeners) l(this.state);
  }

  /**
   * Aplica un snapshot SOLO si pertenece a la sala que el cliente tiene.
   * Sin este filtro, un broadcast tardío de una sala abandonada (o de otra
   * sala en la que el socket quedó suscrito) redirigía la UI al estado de
   * una partida en la que el jugador ya no está.
   */
  private applySnapshot(snap: GameStateSnapshot) {
    if (this.expectedRoomId) {
      // Estamos entrando a una sala conocida: solo se aplica su estado.
      if (snap.roomId !== this.expectedRoomId) return;
    } else if (this.enteringRoom) {
      // Estamos creando una sala y todavía no conocemos su id: el socket
      // pertenece a una sola sala, así que se adopta la que no era la
      // anterior (la anterior sigue siendo nuestra hasta el ack).
      if (this.currentRoomId && snap.roomId === this.currentRoomId) return;
    } else if (!this.currentRoomId || snap.roomId !== this.currentRoomId) {
      return;
    }
    // Mientras la entrada está pendiente NO se adopta la sala nueva: si la
    // entrada se rechaza, el cliente tiene que volver a la sala anterior.
    // La adopción definitive llega con el ack, que sí confirma la entrada.
    if (!this.enteringRoom) this.currentRoomId = snap.roomId;
    this.update({ state: snap });
  }

  private beginEntering(roomId: RoomId | null) {
    this.expectedRoomId = roomId;
    this.enteringRoom = true;
    this.previousRoomState = this.state.state;
  }

  /**
   * Cierra la ventana de entrada. Si la entrada fue rechazada, se restaura
   * el estado de la sala anterior: sin esto, un snapshot que llegó durante la
   * entrada dejaría la UI apuntando a una sala a la que nunca se entró.
   */
  private endEntering(success = true) {
    this.enteringRoom = false;
    this.expectedRoomId = null;
    if (!success) {
      this.update({ state: this.previousRoomState });
    }
    this.previousRoomState = null;
  }

  /**
   * Fuerza un ciclo disconnect + connect del socket sin recargar la página.
   * Equivalente programático a un F5 desde el punto de vista de la conexión,
   * pero conserva el estado de Lit y la session de localStorage.
   * El flag `wasDisconnected` se setea en true durante el disconnect y al
   * volver el connect se re-emite `room:rejoin` automáticamente.
   */
  forceReconnect(): void {
    const s = this.socket;
    if (!s) return;
    s.disconnect();
    s.connect();
  }

  /**
   * Espera a que el socket esté conectado.
   *
   * La entrada a una sala necesita el socket: si el evento sale antes de
   * conectar, el servidor nunca responde y la entrada queda colgada. Se espera
   * un tiempo acotado y, si el socket no llega, se devuelve un error claro en
   * vez de dejar la pantalla esperando para siempre.
   */
  private waitForConnection(timeoutMs = 5000): Promise<boolean> {
    const s = this.socket;
    if (!s) return Promise.resolve(false);
    if (this.state.connected) return Promise.resolve(true);
    return new Promise((resolve) => {
      const onConnect = () => {
        clearTimeout(timer);
        resolve(true);
      };
      const timer = setTimeout(() => {
        s.off('connect', onConnect);
        resolve(false);
      }, timeoutMs);
      s.on('connect', onConnect);
    });
  }

  /**
   * Envía un evento al servidor. Devuelve `false` si NO se pudo enviar (no
   * hay socket): los que esperan un ack (create/join/rejoin) lo usan para no
   * quedar esperando una respuesta que nunca va a llegar.
   */
  send<E extends keyof ClientToServerEvents>(
    event: E,
    ...args: Parameters<ClientToServerEvents[E]>
  ): boolean {
    const s = this.socket;
    if (!s) return false;
    (s.emit as (event: E, ...args: unknown[]) => void)(event, ...args);
    return true;
  }

  async createRoom(displayName: string): Promise<{ ok: true; roomId: RoomId; playerId: PlayerId; sessionToken: string } | { ok: false; error: string }> {
    this.beginEntering(null);
    if (!(await this.waitForConnection())) {
      this.endEntering(false);
      return { ok: false, error: 'Sin conexión con el servidor' };
    }
    return new Promise((resolve) => {
      const sent = this.send('room:create', { displayName }, (resp) => {
        if (resp.ok) this.currentRoomId = resp.roomId;
        this.endEntering(resp.ok);
        resolve(resp);
      });
      // Sin socket no hay ack: sin esto la promesa no resolvía nunca y la
      // pantalla de entrada quedaba colgada para siempre.
      if (!sent) {
        this.endEntering(false);
        resolve({ ok: false, error: 'Sin conexión con el servidor' });
      }
    });
  }

  async joinRoom(roomId: RoomId, displayName: string): Promise<{ ok: true; playerId: PlayerId; state: GameStateSnapshot; sessionToken: string } | { ok: false; error: string }> {
    this.beginEntering(roomId);
    if (!(await this.waitForConnection())) {
      this.endEntering(false);
      return { ok: false, error: 'Sin conexión con el servidor' };
    }
    return new Promise((resolve) => {
      const sent = this.send('room:join', { roomId, displayName }, (resp) => {
        if (resp.ok) {
          this.currentRoomId = resp.state.roomId;
          this.update({ state: resp.state });
        }
        this.endEntering(resp.ok);
        resolve(resp);
      });
      if (!sent) {
        this.endEntering(false);
        resolve({ ok: false, error: 'Sin conexión con el servidor' });
      }
    });
  }

  async rejoinRoom(roomId: RoomId, playerId: PlayerId, displayName: string, sessionToken: string): Promise<{ ok: true; playerId: PlayerId; state: GameStateSnapshot } | { ok: false; error: string }> {
    this.beginEntering(roomId);
    if (!(await this.waitForConnection())) {
      this.endEntering(false);
      return { ok: false, error: 'Sin conexión con el servidor' };
    }
    return new Promise((resolve) => {
      const sent = this.send('room:rejoin', { roomId, playerId, displayName, sessionToken }, (resp) => {
        if (resp.ok) {
          this.currentRoomId = resp.state.roomId;
          this.update({ state: resp.state });
        }
        this.endEntering(resp.ok);
        resolve(resp);
      });
      if (!sent) {
        this.endEntering(false);
        resolve({ ok: false, error: 'Sin conexión con el servidor' });
      }
    });
  }
}

export const gameStore = new GameStore();
